// SD57 operator media processing: client-side checks and plain-language summaries for the
// /ops/media form. The server (server/src/media_processing.rs `put_settings`) is authoritative
// and re-validates everything; these only stop an obviously broken save before the round trip.
import type { OpsMediaSettingsPut, ProcessingMode } from "../api/types";

export interface MediaFormValues {
  muxEnabled: boolean;
  muxMode: ProcessingMode;
  autoBudgetSecs: string;
  throughputMbps: string;
  overheadSecs: string;
  transcodeEnabled: boolean;
  transcodeMode: ProcessingMode;
  rungs: string[];
  preset: string;
  rungConcurrency: string;
}

export type MediaFormErrors = Partial<Record<keyof MediaFormValues, string>>;

/** The form shows throughput in MB/s (decimal, like the server's bytes/sec constant) because
 * "6000000" is unreadable; the API stores bytes/sec. */
export function bytesToMbps(bytes: number): string {
  return String(Math.round((bytes / 1_000_000) * 100) / 100);
}

function wholeNumber(raw: string): number | null {
  const t = raw.trim();
  return /^\d+$/.test(t) ? Number(t) : null;
}

export const NO_RUNGS_MESSAGE = "Choose at least one quality level, or turn transcoding off.";

export function validateMedia(v: MediaFormValues, maxConcurrency: number): MediaFormErrors {
  const errors: MediaFormErrors = {};
  const budget = wholeNumber(v.autoBudgetSecs);
  if (budget === null || budget > 86_400) errors.autoBudgetSecs = "Seconds, between 0 and 86400.";
  const mbps = Number(v.throughputMbps.trim());
  if (!v.throughputMbps.trim() || !Number.isFinite(mbps) || mbps <= 0) {
    errors.throughputMbps = "A positive number of MB/s.";
  }
  const overhead = wholeNumber(v.overheadSecs);
  if (overhead === null || overhead > 3_600) errors.overheadSecs = "Seconds, between 0 and 3600.";
  if (v.rungs.length === 0) errors.rungs = NO_RUNGS_MESSAGE;
  const conc = wholeNumber(v.rungConcurrency);
  if (conc === null || conc < 1 || conc > maxConcurrency) {
    errors.rungConcurrency = `Between 1 and ${maxConcurrency}.`;
  }
  return errors;
}

export function toPut(v: MediaFormValues): OpsMediaSettingsPut {
  return {
    mux: {
      enabled: v.muxEnabled,
      mode: v.muxMode,
      auto_budget_secs: Number(v.autoBudgetSecs.trim()),
      throughput_bytes_per_sec: Math.round(Number(v.throughputMbps.trim()) * 1_000_000),
      overhead_secs: Number(v.overheadSecs.trim()),
    },
    transcode: {
      enabled: v.transcodeEnabled,
      mode: v.transcodeMode,
      rungs: v.rungs,
      preset: v.preset,
      rung_concurrency: Number(v.rungConcurrency.trim()),
    },
  };
}

/** One sentence per kind, saying what will actually happen to the next recording. */
export function describeMux(v: Pick<MediaFormValues, "muxEnabled" | "muxMode" | "transcodeEnabled">): string {
  if (!v.muxEnabled) {
    return "Off. Recordings play progressively from the uploaded file; nobody can ask for streaming segments.";
  }
  const encode = v.transcodeEnabled
    ? "Non-H.264 recordings are encoded once when asked for."
    : "Non-H.264 recordings can't be muxed while transcoding is off.";
  return v.muxMode === "automatic"
    ? `Automatic. H.264 recordings within the cost budget are muxed during publish; larger ones wait to be asked for. ${encode}`
    : `On demand. Nothing is muxed until someone with the processing permission asks. ${encode}`;
}

export function describeTranscode(v: Pick<MediaFormValues, "transcodeEnabled" | "transcodeMode">): string {
  if (!v.transcodeEnabled) return "Off. No video is ever re-encoded; no quality levels can be generated.";
  return v.transcodeMode === "automatic"
    ? "Automatic. Every published recording gets the selected quality levels, queued right after publish."
    : "On demand. Quality levels are generated only when someone with the processing permission asks.";
}
